import { useState } from "react";
import { IonHeader, IonTitle, IonToolbar } from "@ionic/react";
import { Loader } from "components";
import { useRequests } from "pages/RequestIndex/hooks";
import { NoRequestsWithSearchTerm } from "pages/RequestIndex/NoRequests";
import { RequestCount } from "pages/RequestIndex/RequestCount";
import { RequestIndex } from "pages/RequestIndex/RequestIndex";
import { useFeatureFlags } from "swing-components";

import { DateReel, FilterBar, toRequestFilters } from "~components/Organisms/IndexFiltersV2";
import {
  initialFilterValues,
  RequestIndexPage as RequestIndexPageType,
} from "~components/Organisms/IndexFiltersV2/types";
import { IndexSearch } from "~components/Organisms/IndexSearch";
import { translate, useLocalStorage } from "~utils";

type RequestIndexPageProps = {
  page: RequestIndexPageType;
};

/**
 * v2 of the request index, serving both the active and past pages.
 *
 * Everything that differs between the two is derived from `page`, so unlike v1
 * there is a single page component rather than one per request type.
 */
export function RequestIndexPage(props: RequestIndexPageProps) {
  const { page } = props;

  const [ls, setLs] = useLocalStorage(`swing-schools:filters:v2:${page}`, initialFilterValues);

  const [searchTerm, setSearchTerm] = useState("");

  const { flags } = useFeatureFlags();
  const showSearch = flags.adminIndexSearch;

  const { data, isError, isFetching } = useRequests({
    showHistory: page === "past",
    filters: toRequestFilters(ls),
    searchTerm: showSearch ? searchTerm : undefined,
  });

  if (!data) return <Loader />;

  const { requests } = data;

  return (
    <>
      <IonHeader collapse="condense">
        <IonToolbar>
          <IonTitle size="large">
            {translate(
              page === "active" ? "ROUTE_TABLE_ACTIVE_REQUESTS" : "ROUTE_TABLE_PAST_REQUESTS",
            )}
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <DateReel page={page} ls={ls} setLs={setLs} />
      {showSearch && (
        // The reel sits above the search on this page, so the search needs the
        // top margin it does not have in v1.
        <div css={{ marginTop: 16 }}>
          <IndexSearch searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        </div>
      )}
      <FilterBar page={page} ls={ls} setLs={setLs} />
      {isFetching && searchTerm ? (
        <NoRequestsWithSearchTerm searchTerm={searchTerm} page={page} />
      ) : (
        <>
          <RequestCount page={page} count={data.total} />
          <RequestIndex
            requestType={page === "active" ? "active-requests" : "past-requests"}
            requests={requests}
            isError={isError}
            hasNextPage={data.hasNextPage}
            fetchNextPage={data.fetchNextPage}
            isFetchingNextPage={data.isFetchingNextPage}
          />
        </>
      )}
    </>
  );
}
